import { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, InlineDialog } from './ui';
import { DLG } from './dialogStyles';
import AssignedSetBox from './AssignedSetBox';
import { teacherApi } from '../data/api';
import { dateLabel } from '../data/teacherHomeModel';
import { dueLocalValue } from '../data/homeworkEdit';

/* Buffy's editHomework action (2026-10-16): the assigned-set edit box opens
   in the chat with Buffy's change already filled in (a new name, due date,
   close at the due date, a start, or the student list open). The teacher
   checks it and presses Save changes; nothing changes before that. Same box,
   same updateHomework call and rules as Edit on the Assignments page. Buffy
   names the set by id or exact title; otherwise the teacher picks one. */

// Buffy's change as the box's starting values. undefined = keep; null = remove
// the due date / open the set now.
const initialOf = p => ({
  ...(p.name ? { name: p.name } : {}),
  ...(p.due !== undefined ? { due: p.due === null ? '' : dueLocalValue(p.due) } : {}),
  ...(p.start !== undefined ? { start: p.start === null ? '' : dueLocalValue(p.start) } : {}),
  ...(p.closeAtDue !== undefined ? { closeAtDue: p.closeAtDue } : {}),
  ...(p.students ? { showStudents: true } : {}),
});

export default function EditHomeworkChat({ params, auth, classId, onClose, onDone }) {
  const inline = useContext(InlineDialog);
  const [open, setOpen] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [target, setTarget] = useState(null);
  const [rev, setRev] = useState(0);
  useEffect(() => {
    let alive = true;
    teacherApi(auth, 'listHomework', { classID: classId })
      .then(r => {
        if (!alive) return;
        const items = (r?.items || []).filter(hw => hw.status === 'open');
        setOpen(items);
        const wanted = String(params.title || '').trim().toLowerCase();
        const hit = items.find(hw => (params.homework && hw.hwId === params.homework) || (wanted && String(hw.title || '').trim().toLowerCase() === wanted));
        setTarget(t => (t && rev ? items.find(hw => hw.hwId === t.hwId) || null : hit || (items.length === 1 && !params.homework && !wanted ? items[0] : null)));
      })
      .catch(() => { if (alive) setLoadError(true); });
    return () => { alive = false; };
  }, [auth, classId, params.homework, params.title, rev]); // eslint-disable-line react-hooks/exhaustive-deps

  const saved = r => {
    const title = r?.hw?.title || target.title;
    const purged = Array.isArray(r?.purged) ? r.purged.length : Number(r?.purged) || 0;
    onDone(`Saved changes to the homework "${title}".${purged ? ` ${purged} student${purged === 1 ? ' was' : 's were'} removed; their answers were erased.` : ''}`, `Saved “${title}”`);
    onClose();
  };
  const body = target
    ? <AssignedSetBox key={`${target.hwId}:${rev}`} hw={target} auth={auth} classId={classId} initial={rev ? null : initialOf(params)} fromBuffy={!rev} focusName={false}
      onClose={onClose} onSaved={saved} onReload={() => setRev(n => n + 1)} />
    : <div className={inline ? 'flex flex-col gap-[8px]' : `${DLG.body} flex flex-col gap-[8px]`}>
      {inline && <p className="m-0 text-[14px] font-semibold text-ink">Which practice set should change?</p>}
      {loadError && <p className="m-0 text-[13px] text-error-600">Homework updates are unavailable. Your assignments have not changed.</p>}
      {!loadError && !open && <p className="m-0 text-[13px] text-neutral-500" role="status">Loading practice assignments…</p>}
      {open && !open.length && <p className="m-0 text-[13px] text-neutral-500">There are no open practice assignments in this class.</p>}
      {(open || []).map(hw => <button key={hw.hwId} type="button" onClick={() => setTarget(hw)}
        className="flex items-center justify-between gap-[12px] w-full text-left px-[16px] py-[12px] rounded-[10px] ring-1 ring-inset ring-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer border-0">
        <span className="min-w-0"><span className="block text-[14px] font-semibold text-ink truncate">{hw.title}</span>
          <span className="block text-[12px] text-neutral-500">{hw.dueDate ? `Due ${dateLabel(hw.dueDate)}` : 'No due date'}</span></span>
      </button>)}
    </div>;
  if (inline) return body;
  return <Modal open onClose={onClose} size="lg">
    {!target && <ModalHeader title="Edit a practice set" sub="Pick an open practice assignment" pad={DLG.head} onClose={onClose} />}
    <div className={target ? 'p-[16px] overflow-y-auto' : undefined}>{body}</div>
  </Modal>;
}
